(ns ycweb.app.website.cart.pages.details-address
  (:require
   #?@(:cljs
       [["react-google-autocomplete" :as autocomplete]])
   [clojure.set :as set]
   [clojure.string :as str]
   [rum.core :as rum]
   [ycweb.app.website.cart.addresses :as addresses]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.core :as u]))

;; --- Google Address Helper Functions ----

(defn google-address [place]
  #?(:cljs
     (let [find-type
           (fn [component-types]
             (->> (:address_components place)
               (filter #(seq (set/intersection (set (:types %)) (set component-types))))
               (map :long_name)
               (str/join ", ")))
           location (get-in place [:geometry :location])]

       {:googleaddress (:formatted_address place)
        :address (u/glue " " (find-type ["street_number"]) (find-type ["route"]))
        :suburb (find-type ["sublocality" "sublocality_level_2"])
        :city (find-type ["locality" "postal_town"])
        :province (find-type ["administrative_area_level_1"])
        :postalcode (find-type ["postal_code"])
        :longitude (let [lng (aget location "lng")] (if (fn? lng) (lng) lng))
        :latitude (let [lat (aget location "lat")] (if (fn? lat) (lat) lat))})))

(defn country-code
  "Get the country code for a given country UUID from the app state.
   Defaults to 'za' (South Africa) if country not found or UUID is nil."
  [app-atom country-uuid]
  (if country-uuid
    (let [countries (cmn/curact app-atom [:addresses :countries])
          country (get countries country-uuid)]

      (str/lower-case (or (:country_code country) "za")))
    "za"))

(rum/defc google-autocomplete < rum/reactive
  [app-atom address-atom]

  (let [code (country-code app-atom (cmn/curact address-atom [:country_uuid]))]

    [:div.field-highlight
     (forms/form-field
       "Search for your street address to autofill the fields below"
       {}
       [:div.text-input-wrapper
        (rum/adapt-class
          #?(:cljs
             autocomplete/default
             :clj
             :<>)
          {:apiKey "AIzaSyAOCQrlmxLRaX5Kw_JY3-5fiVtGGPeLKss"
           :class "text-input text-input--has-icon text-input--location text-input--address"
           :options
           {:types #?(:cljs #js ["address"] :clj ["address"])
            :componentRestrictions
            {:country code}}
           :onPlaceSelected
           (fn [place]
             #?(:cljs
                (let [address (google-address (js->clj place :keywordize-keys true))]
                  (swap! address-atom merge
                    (select-keys address
                      [:googleaddress :address :suburb :city :postalcode
                       :longitude :latitude])))))})
        [:i.text-input-icon]]
       [:div.form-hint-wrapper
        [:span.form-hint.u-block
         [:span
          {:class (u/bem :flex-grid-group [:flush :no-wrap :align-left])}
          [:span
           {:class (u/bem :flex-grid-block [:grow-1 :flush :fluid])}
           [:span.flex-grid-content
            "e.g. 8 Street Name, Suburb Name, City Name"]]
          [:span
           {:class
            (u/bem :flex-grid-block [:shrink-0 :align-right :flush :fluid])}
           [:span.flex-grid-content "(Powered by Google)"]]]]])]))

(rum/defc address-option < rum/reactive cmn/cursors
  [app-atom cart-atom address option-key
   {:keys [selected? on-select on-edit disabled-reason]}]
  (let [id (:uuid address)
        address-type (:addresstype address)
        ;; A disabled-reason greys the option and blocks selection (still editable),
        ;; mirroring the Discovery Miles disabled-with-reason pattern on payments.
        show-disabled? (some? disabled-reason)
        address-name
        (or (u/presence (:company address))
          (str (:firstname address) " " (:lastname address)))]
    [:li {:class (str "checkout-options-list__item card card--no-hover checkout-option checkout-option--address"
                   (when selected? " checkout-option--is-selected")
                   (when show-disabled? " checkout-option--is-disabled")
                   " js-checkout-option")
          :key (str "address-" id)}
     [:label {:for (str "delivery-address-" id) :class "checkout-option__label"}
      [:span {:class "checkout-option__content"}
       [:span {:class "checkout-option__indicator"}
        [:input
         (merge
           (forms/radiobox-props (rum/cursor-in cart-atom [:cart option-key :uuid]) id "deliveryaddressid"
             {:change-fn on-select})
           {:id (str "delivery-address-" id)
            :class "checkout-option__radio"
            :disabled show-disabled?})]
        [:span {:class "checkout-option__radio-graphic"}]]
       [:span {:class "checkout-option__text"}
        [:span {:class "checkout-option__title"}
         address-name " "
         ;; Only show the type pill when residential/business is actually set —
         ;; otherwise it renders as an empty black pill.
         (when (u/presence address-type)
           [:span
            {:class
             (str "text-pill text-pill--default "
               (if (= address-type "Business")
                 "text-pill--important"
                 "text-pill--dark"))}
            [:span {:class "u-hidden-from-view"} "— "]
            address-type])
         [:span {:class "u-hidden-from-view"} "."]]
        [:span {:class "u-hidden-from-view"} "Address:"]
        [:span {:class "checkout-option__description"}
         ;; Postal code on its own line (no longer glued to the city).
         (for [[i line] (->>
                          [(:address address) (:suburb address) (:city address) (:postalcode address)]
                          (map u/presence)
                          (remove nil?)
                          (map-indexed vector))]
           [:span {:key i} (when (pos? i) [:br]) line])]
        ;; Country — shown above the phone number.
        (when-let [country-name (:name (get (cmn/curact app-atom [:addresses :countries])
                                         (:country_uuid address)))]
          [:span {:class "checkout-option__description"} country-name])
        [:span {:class "u-hidden-from-view"} "Phone number:"]
        [:span {:class "checkout-option__description"}
         (or (:cell address) (:tel address))]
        (when (or (:default-billing address) (:default-delivery address))
          [:span {:class "checkout-option__defaults"}
           (when (:default-billing address)
             [:span {:class "text-pill text-pill--default text-pill--light"}
              [:i {:class "icon  icon--inline icon--in-copy-left icon--tiny icon--location"}]
              "Default billing"])
           (when (:default-delivery address)
             (list " "
               [:span {:class "text-pill text-pill--default text-pill--light"}
                [:i {:class "icon  icon--inline icon--in-copy-left icon--tiny icon--location"}]
                "Default delivery"]))])
        ;; Reason this address can't be used (e.g. non-SA on the delivery step).
        (when show-disabled?
          [:span {:class "checkout-option__description checkout-option__description--warning"} disabled-reason])]]]
     [:nav {:class "checkout-option__nav"}
      [:a {:href "#"
           :class "cart-item-options__link icon-link icon-block-link js-open-modal-add-address"
           :on-click on-edit}
       [:i {:class "icon icon--inline icon--is-dark icon--pencil"}]
       [:span {:class "u-hidden-from-view"} "Edit address"]]]]))

(rum/defc add-address-cta < rum/reactive cmn/cursors [app-atom cart-atom option-key]
  (let [logged-in? (addresses/is-user-logged-in? cart-atom)
        saved-addresses (addresses/address-list-atom app-atom)]

    (when (or logged-in? (empty? @saved-addresses))
      [:div {:class "card card--no-hover checkout-callout-card text-card text-card--medium"}
       [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--valign-middle"}
        [:div {:class "flex-grid-block flex-grid-block--l-2-3"}
         [:div {:class "flex-grid-content"}
          [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--no-wrap flex-grid-group--valign-middle"}
           [:div {:class "flex-grid-block flex-grid-block--fluid flex-grid-block--shrink-0"}
            [:div {:class "flex-grid-content"}
             [:i {:class "icon icon--block icon--medium-large icon--location"}]]]
           [:div {:class "flex-grid-block flex-grid-block--fluid flex-grid-block--grow-1"}
            [:div {:class "flex-grid-content"}
             [:p {:class "yc-paragraph yc-paragraph--flush js-voucher-text-not-redeemed"}
              [:b "Use an existing address or add a new one."]]]]]]]
        [:div {:class "flex-grid-block flex-grid-block--l-1-3"}
         [:div {:class "flex-grid-content"}
          (buttons/button "Add address"
            #(grid/open-modal app-atom :cart/address-form
               {:editing? false
                :option-key option-key
                :address
                {:addresstype "Residential"
                 :country_uuid (addresses/default-country-uuid app-atom)}})
            {:icon-left :plus
             :icon-class :is-light
             :call-to-action true
             :raised true
             :full true})]]]])))

(defn valid-phone?
  "Matches checkout validation's rule: at least 9 digits, ignoring formatting"
  [v]
  (>= (count (str/replace (str v) #"[^0-9]" "")) 9))

(defn address-missing-recipient?
  "True when the address lacks the recipient details checkout validation requires —
   a name for both steps, plus a valid cellphone for delivery. Legacy address-book
   rows can be missing these; selecting one sails through to the final checkout
   validation block, so we surface it here instead. Checkout validates :cell only
   (never :tel), so a :tel-only address must also be caught."
  [option-key address]
  (or
    (str/blank? (str (:firstname address)))
    (str/blank? (str (:lastname address)))
    (and (= option-key :delivery)
      (not (valid-phone? (:cell address))))))

(defn address-missing-location?
  "Door-delivery checkout also requires the address fields themselves —
   legacy address-book rows can be missing suburb/city/postal code. Only the
   delivery picker gates on this (billing needs no street address), and it only
   renders for door delivery (collect/postnet use the collection form)."
  [option-key address]
  (and (= option-key :delivery)
    (or
      (str/blank? (str (:address address)))
      (str/blank? (str (:suburb address)))
      (str/blank? (str (:city address)))
      (str/blank? (str (:postalcode address))))))

(defn select-default-address
  "Keep/choose a valid selection for this step. Delivery is SA-only, so a non-SA
   address is never auto-selected and an already-selected non-SA address is deselected
   (the user must pick or edit one to an SA address). Addresses missing recipient
   details are likewise never auto-selected and get deselected.
   Idempotent: a no-op once the selection is valid, so it's safe from :did-update."
  [app-atom cart-summary-atom address-list-atom option-key]
  (let [selectable?
        (fn [a]
          (and
            (not (address-missing-recipient? option-key a))
            (not (address-missing-location? option-key a))
            (or (= option-key :billing) (addresses/south-african? app-atom a))))
        addresses-list @address-list-atom
        selected-id (get-in @cart-summary-atom [:cart option-key :uuid])
        selected (first (filter #(= (:uuid %) selected-id) addresses-list))
        default-key (if (= option-key :billing) :default-billing :default-delivery)]
    (cond
      ;; Current selection isn't valid for this step (non-SA on delivery) → deselect.
      (and selected (not (selectable? selected)))
      (swap! cart-summary-atom assoc-in [:cart option-key :uuid] nil)

      ;; Nothing selected → choose the step's default address if it's valid.
      (not selected)
      (when-let [default (first (filter #(and (get % default-key) (selectable? %)) addresses-list))]
        (addresses/select-saved-address app-atom
          (rum/cursor-in cart-summary-atom [:cart option-key])
          default option-key)))))

(rum/defc address-selection-form < rum/reactive cmn/cursors
  {:will-mount
   (fn [state]
     (let [[app-atom _ cart-summary-atom _ option-key] (:rum/args state)]
       (select-default-address app-atom cart-summary-atom
         (addresses/address-list-atom app-atom) option-key))
     state)
   :did-update
   (fn [state]
     (let [[app-atom _ cart-summary-atom _ option-key] (:rum/args state)
           address-list @(addresses/address-list-atom app-atom)]
       (when (seq address-list)
         (select-default-address app-atom cart-summary-atom
           (addresses/address-list-atom app-atom) option-key)))
     state)}
  [app-atom process-atom cart-summary-atom title option-key]
  (let [{:keys [user] :as cart-summary} (rum/react cart-summary-atom)
        ;; Get saved addresses from API
        saved-addresses (addresses/address-list-atom app-atom)
        selected-address-id (cmn/curact cart-summary-atom [:cart option-key :uuid])

        ;; On the delivery step a non-SA address can't be used (we only ship within
        ;; SA) so it renders disabled. Addresses missing recipient details (legacy
        ;; address-book rows) are disabled on both steps until edited — checkout
        ;; validation would block them at the final step otherwise.
        disabled-address-reason
        (fn [address]
          (cond
            (and (= option-key :delivery)
              (not (addresses/south-african? app-atom address)))
            "We only deliver within South Africa. Edit this address and set the country to South Africa to use it for delivery."

            (address-missing-recipient? option-key address)
            "This address is missing a recipient name or valid cellphone number. Edit it to add the missing details."

            (address-missing-location? option-key address)
            "This address is missing some details (street, suburb, city or postal code). Edit it to complete it."

            :else nil))

        ;; Order the list: enabled (selectable) addresses first, disabled ones last,
        ;; and within each group float this step's default address to the top —
        ;; billing default on billing, delivery default on delivery. Stable sort keeps
        ;; the remaining addresses in their original order.
        default-key (if (= option-key :billing) :default-billing :default-delivery)
        sorted-addresses
        (sort-by
          (juxt #(if (disabled-address-reason %) 1 0)
            #(if (get % default-key) 0 1))
          ;; rum/react (not @) so the list re-renders when the saved addresses are
          ;; re-fetched after a save — otherwise an updated default/flag won't show
          ;; until the next cart-summary change.
          (rum/react saved-addresses))

        show-email-field?
        (and
          (not (addresses/is-user-logged-in? cart-summary-atom))
          (= option-key :billing))

        ;; Check if user is logged in
        on-address-select
        (fn [address-id]
          (let [address-data (first (filter #(= (:uuid %) address-id) @saved-addresses))
                address-atom (rum/cursor-in cart-summary-atom [:cart option-key])]
            (when address-data
              ;; Update cart with address_id for selection
              (swap! cart-summary-atom assoc-in [:cart option-key :address_id] (str address-id))
              ;; Call select-saved-address to store the selection for future page loads
              (addresses/select-saved-address app-atom address-atom address-data option-key)
              ;; Save changes to database
              (model/update-cart-delivery-details app-atom))))]

    (cart.common/checkout-layout process-atom true
      (list
        (when
          show-email-field?
          (list
            [:h2 {:key "email-heading" :class "yc-heading yc-heading--default"}
             "We'll send updates on your order to your email."]
            [:div {:key "email-field" :class "card card--no-hover checkout-callout-card text-card text-card--medium"}
             [:div {:class "form-field form-field--flush"}
              (forms/input "Your email address: (required)" ""
                (cmn/cursor-in cart-summary-atom [:cart :billing :email])
                {:type :email :required? true :flush? true})]]))

        [:h2 {:key "address-heading" :class "yc-heading yc-heading--default"} title]

        [:ul {:key "address-list" :class "checkout-options-list"}
         (for [address sorted-addresses]
           (address-option
             app-atom
             cart-summary-atom
             address option-key
             {:selected? (= (:uuid address) selected-address-id)
              ;; Greys out (but still lets you edit) addresses that can't be used on
              ;; this step: non-SA on delivery, or missing recipient details.
              :disabled-reason (disabled-address-reason address)
              :on-select #(on-address-select (:uuid address))
              :on-edit
              #(grid/open-modal app-atom :cart/address-form
                 {:editing? true
                  :option-key option-key
                  :address-id (:uuid address)
                  :address address})}))]

        (rum/with-key (add-address-cta app-atom cart-summary-atom option-key) "add-address"))

      (cart.common/checkout-cta-sidebar app-atom process-atom cart-summary-atom
        {:disabled?
         (or
           (not selected-address-id)
           (and
             show-email-field?
             (str/blank? (cmn/curact cart-summary-atom [:cart :billing :email]))))

         :notice
         (when
           (and
             (not selected-address-id)
             (seq sorted-addresses)
             (every? disabled-address-reason sorted-addresses)
             (boolean
               (some
                 #(or
                    (address-missing-recipient? option-key %)
                    (address-missing-location? option-key %))
                 sorted-addresses)))
           [:p {:class "yc-paragraph yc-paragraph--small u-text-red"}
            "Your saved address is missing required details — edit it to continue."])}))))

(rum/defc recipient-collection-form < rum/reactive cmn/cursors [app-atom process-atom cart-summary-atom]
  "Collection recipient form for store pickup - matches delivery-step-02b-recipient-collection.html"
  (let [{:keys [user] :as cart-summary} (rum/react cart-summary-atom)
        form-data (cmn/curact cart-summary-atom [:cart :delivery])

        form-valid?
        (and
          (not (str/blank? (str/trim (str (:firstname form-data)))))
          (not (str/blank? (str/trim (str (:lastname form-data)))))
          (valid-phone? (:cell form-data)))]

    [:main {:id "main-content" :class "yc-section"}
     [:form {:class "flex-grid-group"}

      ;; Left column: Recipient details (3/4 width)
      [:div {:class "flex-grid-block flex-grid-block--ml-3-4 flex-grid-block--l-2-3"}
       [:div {:class "flex-grid-content"}
        [:div {:class "u-max-width-480 u-margin-bottom-40"}
         [:h2 {:class "yc-heading yc-heading--default"}
          "Who's picking up this order when it's ready to collect?"]

         ;; Name fields (side by side)
         (forms/split-input-field
           ["First name" "e.g. Jane" (cmn/cursor-in cart-summary-atom [:cart :delivery :firstname]) {:type :user :required? true}]
           ["Last name" "e.g. Smith" (cmn/cursor-in cart-summary-atom [:cart :delivery :lastname]) {:type :user :required? true}])

         ;; Cellphone field
         (forms/input "Cellphone" "" (cmn/cursor-in cart-summary-atom [:cart :delivery :cell]) {:type :phone :required? true})]]]

      ;; Right column: Standard CTA sidebar
      [:div {:class "flex-grid-block flex-grid-block--t-full flex-grid-block--l-1-3"}
       (cart.common/checkout-cta-sidebar app-atom process-atom cart-summary-atom
         {:disabled? (not form-valid?)})]]]))

;; Own reactive component so it re-renders when the country changes (like the
;; select does) — the inline version inside the modal body didn't update reliably.
(rum/defc country-ship-warning < rum/reactive [app-atom country-uuid-cursor]
  ;; Reacts to the SAME cursor the country select writes (passed in), so it updates
  ;; the instant the country changes — mirroring how forms/select stays live.
  ;; Only warns for a real, non-SA country (not the "Please select a country"
  ;; placeholder, which the disabled Save button covers).
  (let [country-uuid (rum/react country-uuid-cursor)
        country (addresses/find-country app-atom country-uuid)
        sa-code (get-in @app-atom [:config :sa-country-code])]
    (when (and country (not= sa-code (:country_code country)))
      [:div.u-margin-top-20
       (feedback/feedback :warning "We don't ship outside of South Africa"
         "You're welcome to set this address as your billing address, but we do not ship outside of South Africa.")])))

(rum/defc default-delivery-field < rum/reactive [app-atom country-uuid-cursor default-delivery-cursor]
  ;; We only ship within South Africa, so a non-SA address can't be the default
  ;; delivery address. Reacts to the country cursor (the select writes it) so the
  ;; field updates the instant the country changes. When non-SA we force the flag
  ;; off (idempotent, mirroring input-props' :default reset) so it shows unchecked
  ;; and never saves as default delivery — covers re-editing an already-non-SA
  ;; address too — and disable the box so it can't be re-checked while non-SA.
  (let [country (addresses/find-country app-atom (rum/react country-uuid-cursor))
        sa-code (get-in @app-atom [:config :sa-country-code])
        non-sa? (boolean (and country (not= sa-code (:country_code country))))]
    (when (and non-sa? (rum/react default-delivery-cursor))
      (reset! default-delivery-cursor false))
    (forms/form-field "" {}
      (forms/checkbox "Make this my default delivery address" default-delivery-cursor
        {:disabled non-sa?}))))

(defn missing-required-fields
  "Human-readable labels of the required fields not yet completed for an address.
   Drives both the Save-button gate (footer) and the hint that explains why Save is
   disabled (body), so the two can't drift apart. A real country must be chosen —
   not the \"Please select a country\" placeholder (value 0)."
  [address]
  (let [valid-country?
        (let [c (str (:country_uuid address))]
          (and (u/presence c) (not= "0" c)))]
    (cond-> (->> [[:firstname "first name"]
                  [:lastname "last name"]
                  [:cell "valid cellphone number"]
                  [:address "street address"]
                  [:suburb "suburb"]
                  [:city "city"]]
              (filter
                (fn [[k _]]
                  ;; Cellphone must satisfy checkout's ≥9-digit rule, not just
                  ;; be present — a short number saved here loops back to the
                  ;; disabled-address warning
                  (if (= k :cell)
                    (not (valid-phone? (get address k)))
                    (str/blank? (str (get address k))))))
              (mapv second))
      (not valid-country?) (conj "country"))))

(rum/defc address-form-modal-body < rum/reactive cmn/cursors [app-atom config-atom _]
  (let [address (cmn/curact config-atom :address)
        option-key (cmn/curact config-atom :option-key)]

    [:form {:ref (fn [node] (swap! config-atom assoc :form-node node))}
      ;; Personal details section
     [:p {:class "yc-paragraph"} [:b "Whose address is this?"]]

     ;; Name fields (side by side)
     (forms/split-input-field
       ["First name" "e.g. Jane" (cmn/cursor-in config-atom [:address :firstname]) {:type :user :required? true}]
       ["Last name" "e.g. Smith" (cmn/cursor-in config-atom [:address :lastname]) {:type :user :required? true}])

     (forms/input "Cellphone" "" (cmn/cursor-in config-atom [:address :cell]) {:type :phone :required? true})

     ;; We don't have this through to the db, so we're commenting it out for now.
     #_(forms/input "Land line phone" "" (cmn/cursor-in config-atom [:address :tel]) {:type :phone})

     [:hr {:class "u-hr"}]

      ;; Address details section
     [:p {:class "yc-paragraph"} [:b "What's the address?"]]
     (forms/radios
       (cmn/cursor-in config-atom [:address :addresstype])
       [{:label "Residential address" :value "Residential"}
        {:label "Business address" :value "Business"}]
       {:selected (get address :addresstype "Residential")})

     ;; Google address autocomplete
     [:div {:class "form-field"}
      (google-autocomplete app-atom (cmn/cursor-in config-atom [:address]))]

     #_(forms/input "Building / Complex / Estate name" "e.g. Blue Waters Estate" (cmn/cursor-in config-atom [:address :building]) {})

     (forms/textarea "Street address" (cmn/cursor-in config-atom [:address :address]) {:rows 2 :required? true :placeholder "e.g. 123 Main Street"})

     (forms/input "Suburb" "e.g. Constantia" (cmn/cursor-in config-atom [:address :suburb]) {:required? true})

     (forms/input "City" "e.g. Cape Town" (cmn/cursor-in config-atom [:address :city]) {:required? true})

     (forms/input "Postal code" "e.g. 7441" (cmn/cursor-in config-atom [:address :postalcode]) {:type :text-number :required? true})

     ;; Full country list, showing the address's real country. A disabled "Please
     ;; select a country" placeholder (value 0) is the default when none is set; the
     ;; save validation rejects 0 (see footer). :type :uuid keeps the value a string
     ;; for the option match (country_uuid arrives as a UUID object from EDN reads but
     ;; a <select> matches by string) and normalises picks back to UUIDs.
     (forms/select "Country" (cmn/cursor-in config-atom [:address :country_uuid])
       (into [{:text "Please select a country" :value 0 :disabled true}]
         (mapv (fn [c] {:text (:name c) :value (str (:uuid c))}) (addresses/countries app-atom)))
       {:default 0 :type :uuid})

     ;; Warn when a non-SA country is chosen (mirrors My Account → Addresses).
     (country-ship-warning app-atom (cmn/cursor-in config-atom [:address :country_uuid]))

     [:hr {:class "u-hr"}]

      ;; Business details — only relevant for a business address, so show them only
      ;; when the address type is "Business".
     (when (= "Business" (:addresstype address))
       [:<>
        [:p {:class "yc-paragraph"} [:b "Would you like to add business details?"]]
        [:p {:class "yc-paragraph yc-paragraph--small"}
         "We'll add these details to your invoice, which you can access online after we've completed your order."]
        (forms/input "Company name" "e.g. Smith & Associates Inc" (cmn/cursor-in config-atom [:address :company]) {})

        (forms/input "VAT number" "" (cmn/cursor-in config-atom [:address :vatnumber]) {})

        [:hr {:class "u-hr"}]])

      ;; Settings section
     [:p {:class "yc-paragraph"} [:b "Add some settings for this address…"]]
     (forms/form-field "" {}
       (forms/checkbox "Make this my default billing address" (cmn/cursor-in config-atom [:address :default-billing]) {}))

     (default-delivery-field app-atom
       (cmn/cursor-in config-atom [:address :country_uuid])
       (cmn/cursor-in config-atom [:address :default-delivery]))

     ;; Explain why "Save address" is disabled — list the still-missing required
     ;; fields (this is the same check that gates the footer's Save button).
     (when-let [missing (seq (missing-required-fields address))]
       [:p {:class "yc-paragraph yc-paragraph--small u-text-red"}
        (str "Add your " (str/join ", " missing) " to save this address.")])

     ;; Loading overlay shown while the address saves (fullscreen so it covers the
     ;; whole scrollable modal, not just the part above the fold).
     (toaster/loading-overlay app-atom :web.cart/address-save {:fullscreen? true})]))

(rum/defc address-form-modal-footer < rum/reactive cmn/cursors [app-atom config-atom _]
  (let [address (cmn/curact config-atom :address)
        processing? (cmn/curact config-atom :processing)
        option-key (cmn/curact config-atom :option-key)
        cart-summary-atom (rum/cursor-in app-atom [:cart-summary])
        logged-in? (addresses/is-user-logged-in? cart-summary-atom)

        ;; Save is gated on every required field being completed (incl. a real
        ;; country). The body shows the same missing list as a hint so a disabled
        ;; Save button always explains itself.
        form-valid? (empty? (missing-required-fields address))

        cart-address-atom (rum/cursor-in app-atom [:cart-summary :cart option-key])
        selected-address-atom (rum/cursor-in app-atom [:addresses option-key :selected])

        validate-fn
        (forms/form-validate (cmn/cursor-in config-atom [:form-node])
          (fn []
            (swap! config-atom assoc :processing true)
            (toaster/open-overlay app-atom :web.cart/address-save "Saving address…")
            ;; Only an edit seeds :uuid — shed any selected address's identity,
            ;; else an add-new save updates that saved row in place.
            (swap! cart-address-atom
              #(merge (dissoc % :uuid :display_name)
                 (select-keys address
                   [:uuid :address :suburb :city :postalcode :addresstype
                    :googleaddress :longitude :latitude
                    :firstname :lastname :company :vatnumber :tel :cell
                    :country_uuid :display_name
                    :default-billing :default-delivery])))

            (let [handle-save-success
                  (fn [success? new-uuid]
                    (swap! config-atom assoc :processing false)
                    (toaster/close-overlay app-atom :web.cart/address-save)
                    (grid/close-modal app-atom)
                    (when success?
                      (swap! cart-summary-atom assoc-in [:cart option-key :uuid] (str new-uuid))
                      (let [address-atom (rum/cursor-in cart-summary-atom [:cart option-key])]
                        (when-let [new-address
                                   (first (filter #(= (:uuid %) (str new-uuid))
                                            @(addresses/address-list-atom app-atom)))]
                          (addresses/select-saved-address app-atom address-atom  new-address option-key)))
                      (when logged-in?
                        (addresses/fetch-saved-addresses app-atom))
                      ;; Delivery is SA-only: if this address was just saved with a
                      ;; non-SA country, deselect it (it'll show greyed with a reason).
                      (when (and (= option-key :delivery)
                              (not (addresses/south-african? app-atom {:country_uuid (:country_uuid address)})))
                        (swap! cart-summary-atom assoc-in [:cart :delivery :uuid] nil))))]
              (addresses/save-address app-atom cart-address-atom selected-address-atom handle-save-success))))]

    (if processing?
      [:div {:class "modal__processing-overlay"}
       [:span {:class "modal__processing-icon-wrapper"}
        [:i {:class "icon icon--block icon--large icon--is-dark icon--loading u-animate-spin"}]]]
      (grid/button-row
        {:left-text "Cancel"
         :left-fn #(grid/close-modal app-atom)
         :left-no-icon true
         :right-text "Save address"
         :right-fn
         #?(:clj nil
            :cljs
            (when form-valid? validate-fn))

         :right-disabled? (not form-valid?)}))))

(defmethod grid/modal-config :cart/address-form [app-atom config-atom _]
  (let [config (if (map? config-atom) config-atom @config-atom)
        editing? (get config :editing? false)
        option-key (get config :option-key)

        header-text
        (cond
          (and editing? (= option-key :billing)) "Edit billing address"
          (and editing? (= option-key :delivery)) "Edit delivery address"
          editing? "Edit address"

          (= option-key :billing) "Add billing address"
          (= option-key :delivery) "Add delivery address"
          :else "Add address")]

    {:header header-text
     :body address-form-modal-body
     :footer address-form-modal-footer
     :modal-type :variable
     :large? true
     :close-on-overlay? false}))

(defmethod cart.common/checkout-step :details/address
  [app-atom process-atom cart-atom]
  (let [cart-summary (rum/react cart-atom)
        delivery-method (get-in cart-summary [:cart :delivery :method])]
    (cond
      (contains? #{"collect" "postnet"} delivery-method)
      (recipient-collection-form app-atom process-atom cart-atom)

      :else
      (address-selection-form app-atom process-atom cart-atom "Where is this order going?" :delivery))))